import React, { useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { X } from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import api from "../services/api";
import "./WhatsNewModal.css";

const CATEGORY_COLORS: Record<string, string> = {
  "New Features": "#1E40AF",
  "Bug Fixes": "#DC2626",
  Improvements: "#D97706",
  Security: "#059669",
  "Breaking Changes": "#7C3AED",
};

const WhatsNewModal: React.FC = () => {
  const queryClient = useQueryClient();
  const user = getCurrentUser();

  const { data: seenData } = useQuery(
    ["release-notes-seen-status"],
    () => api.releaseNotes.getSeenStatus(),
    { enabled: !!user, staleTime: 60000 },
  );

  const { data: latestData } = useQuery(
    ["release-notes-latest"],
    () => api.releaseNotes.getLatest(),
    { enabled: !!seenData?.data?.hasUnseen },
  );

  const markSeenMutation = useMutation(
    (version: string) => api.releaseNotes.markSeen(version),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["release-notes-seen-status"]);
      },
    },
  );

  const hasUnseen = seenData?.data?.hasUnseen;
  const latestNote = latestData?.data;
  const dismissedVersion =
    typeof window !== "undefined"
      ? sessionStorage.getItem("whats-new-dismissed")
      : null;
  const isOpen =
    hasUnseen && latestNote && dismissedVersion !== latestNote.version;

  const handleDismiss = useCallback(() => {
    if (latestNote?.version) {
      sessionStorage.setItem("whats-new-dismissed", latestNote.version);
    }
    queryClient.setQueryData(["release-notes-seen-status"], {
      data: { hasUnseen: false },
    });
  }, [latestNote?.version, queryClient]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleDismiss();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleDismiss]);

  if (!isOpen) return null;

  const handleGotIt = () => {
    markSeenMutation.mutate(latestNote.version);
  };

  return (
    <div
      className="whats-new-overlay"
      aria-hidden="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <dialog className="whats-new-modal" open aria-modal="true">
        <div className="whats-new-header">
          <h2>What's New in HireFocuZ</h2>
          <button
            className="whats-new-close"
            onClick={handleDismiss}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="whats-new-meta">
          <span className="whats-new-version">v{latestNote.version}</span>
          <span className="whats-new-date">
            {new Date(latestNote.published_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>

        <h3 className="whats-new-title">{latestNote.title}</h3>

        {latestNote.categories?.length > 0 && (
          <div className="whats-new-categories">
            {latestNote.categories.map((cat: string) => (
              <span
                key={cat}
                className="whats-new-category-chip"
                style={{ backgroundColor: CATEGORY_COLORS[cat] || "#6B7280" }}
              >
                {cat}
              </span>
            ))}
          </div>
        )}

        <div className="whats-new-content">
          {latestNote.content.split("\n").map((line: string, i: number) => {
            const key = `${i}-${line.slice(0, 20)}`;
            if (line.startsWith("## "))
              return <h4 key={key}>{line.slice(3)}</h4>;
            if (line.startsWith("- "))
              return (
                <p key={key} className="whats-new-bullet">
                  {line}
                </p>
              );
            if (line.trim() === "") return <br key={key} />;
            return <p key={key}>{line}</p>;
          })}
        </div>

        <div className="whats-new-actions">
          <button className="whats-new-btn-secondary" onClick={handleDismiss}>
            Maybe Later
          </button>
          <button
            className="whats-new-btn-primary"
            onClick={handleGotIt}
            disabled={markSeenMutation.isLoading}
          >
            {markSeenMutation.isLoading ? "Saving..." : "Got it!"}
          </button>
        </div>
      </dialog>
    </div>
  );
};

export default WhatsNewModal;
